/**
 * ATRA Membership - Integrated with Empire Theme
 * Uses theme patterns and standard event handling
 */
(function() {
  'use strict';
  
  const AtraMembership = {
    init() {
      if (!document.querySelector('[data-section-type="atra-membership"]')) {
        return;
      }
      
      this.cacheSelectors();
      if (!this.pricing) return; // Exit if no pricing section
      
      this.bindEvents();
      this.initStickyBarObserver();
      this.trackEngagement();
    },

    cacheSelectors() {
      this.stickyBar = document.querySelector('.atra-sticky-cta');
      this.pricing = document.querySelector('#pricing');
      this.scrollTriggers = document.querySelectorAll('[data-scroll-to-pricing]');
      this.forms = document.querySelectorAll('.atra-pricing__form');
      this.finalCtaButton = document.querySelector('.atra-final-cta__button--add-to-cart');
      this.hero = document.querySelector('.atra-hero');
    },

    bindEvents() {
      // Handle scroll to pricing buttons
      this.scrollTriggers.forEach(trigger => {
        trigger.addEventListener('click', e => {
          e.preventDefault();
          this.scrollToPricing();
        });
      });

      // Handle form submissions with AJAX
      this.forms.forEach(form => {
        form.addEventListener('submit', e => this.handleFormSubmit(e, form));
      });

      // Handle final CTA button
      if (this.finalCtaButton) {
        this.finalCtaButton.addEventListener('click', e => {
          e.preventDefault();
          this.handleAddToCart(e, this.finalCtaButton);
        });
      }
    },

    initStickyBarObserver() {
      if (!this.stickyBar) return;
      
      const checkVisibility = () => {
        // Hide on desktop
        if (window.innerWidth > 767) {
          this.stickyBar.style.display = 'none';
          return;
        }
        
        // Show on mobile
        this.stickyBar.style.display = '';
        
        // If hero or pricing missing, show sticky bar anyway on mobile
        if (!this.hero || !this.pricing) {
          this.stickyBar.classList.add('is-visible');
          return;
        }

        // Check if hero is visible
        const heroRect = this.hero.getBoundingClientRect();
        const pricingRect = this.pricing.getBoundingClientRect();
        const isHeroVisible = heroRect.top < window.innerHeight && heroRect.bottom > 0;
        const isPricingVisible = pricingRect.top < window.innerHeight && pricingRect.bottom > 0;

        // Show sticky bar if hero is scrolled past or pricing is not yet visible
        if (!isHeroVisible || (isHeroVisible && !isPricingVisible)) {
          this.stickyBar.classList.add('is-visible');
        } else if (isPricingVisible) {
          this.stickyBar.classList.remove('is-visible');
        }
      };

      // Initial check
      checkVisibility();

      // Use IntersectionObserver for better performance
      if (this.hero && this.pricing && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (entry.target === this.hero && !entry.isIntersecting) {
              if (window.innerWidth <= 767) {
                this.stickyBar.classList.add('is-visible');
              }
            } else if (entry.target === this.pricing && entry.isIntersecting) {
              this.stickyBar.classList.remove('is-visible');
            }
          });
        }, { threshold: 0.1, rootMargin: '0px' });

        observer.observe(this.hero);
        observer.observe(this.pricing);
      } else {
        // Fallback to scroll listener if IntersectionObserver not available
        window.addEventListener('scroll', checkVisibility, { passive: true });
      }
      
      // Handle window resize
      window.addEventListener('resize', checkVisibility);
    },

    scrollToPricing() {
      if (!this.pricing) return;
      const offset = 80;
      const targetPosition = this.pricing.offsetTop - offset;
      window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      this.trackEvent('scroll_to_pricing');
    },

    async handleFormSubmit(e, form) {
      e.preventDefault();
      
      const button = form.querySelector('button[type="submit"]');
      if (!button) return;
      
      const variantId = form.querySelector('input[name="id"]')?.value;
      const planType = form.querySelector('input[name="properties[Plan]"]')?.value || form.getAttribute('data-plan') || 'Unknown';
      
      if (!variantId) {
        // Fallback to normal form submission if no variant ID
        form.submit();
        return;
      }

      const originalText = button.textContent;
      button.disabled = true;
      button.classList.add('disabled');
      button.textContent = 'Agregando...';

      this.trackEvent('add_to_cart_attempt', { plan: planType });

      try {
        // Get product handle from form if available
        const productHandle = form.closest('[data-section-type="atra-membership"]')?.querySelector('[data-product-handle]')?.getAttribute('data-product-handle');
        await this.addToCartAjax(variantId, planType, productHandle);
        this.trackEvent('add_to_cart_success', { plan: planType });
        button.textContent = '✓ Agregado';
        setTimeout(() => {
          button.disabled = false;
          button.classList.remove('disabled');
          button.textContent = originalText;
        }, 2000);
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.trackEvent('add_to_cart_error', { plan: planType });
        button.textContent = 'Error. Intenta de nuevo';
        setTimeout(() => {
          button.disabled = false;
          button.classList.remove('disabled');
          button.textContent = originalText;
        }, 2000);
      }
    },

    async handleAddToCart(e, button) {
      const variantId = button.getAttribute('data-variant-id');
      const planType = button.getAttribute('data-plan') || 'Anual';
      const productHandle = button.getAttribute('data-product-handle');
      
      if (!variantId) {
        console.error('No variant ID found');
        return;
      }

      const originalText = button.textContent;
      button.disabled = true;
      button.classList.add('disabled');
      button.textContent = 'Agregando...';

      this.trackEvent('add_to_cart_attempt', { plan: planType, source: 'final_cta' });

      try {
        await this.addToCartAjax(variantId, planType, productHandle);
        this.trackEvent('add_to_cart_success', { plan: planType, source: 'final_cta' });
        button.textContent = '✓ Agregado';
        setTimeout(() => {
          button.disabled = false;
          button.classList.remove('disabled');
          button.textContent = originalText;
        }, 2000);
      } catch (error) {
        console.error('Error adding to cart:', error);
        this.trackEvent('add_to_cart_error', { plan: planType, source: 'final_cta' });
        button.textContent = 'Error. Intenta de nuevo';
        setTimeout(() => {
          button.disabled = false;
          button.classList.remove('disabled');
          button.textContent = originalText;
        }, 2000);
      }
    },

    async addToCartAjax(variantId, planType, productHandle) {
      let sellingPlanId = null;
      
      // If product handle is provided, fetch product data to get selling plan ID
      if (productHandle) {
        try {
          const productResponse = await fetch(`/products/${productHandle}.js`);
          if (productResponse.ok) {
            const productData = await productResponse.json();
            
            // Find the variant and its selling plan
            const variant = productData.variants.find(v => v.id.toString() === variantId.toString());
            if (variant && variant.selling_plan_allocations && variant.selling_plan_allocations.length > 0) {
              // Try to match selling plan by name (Anual/Mensual)
              // Check selling plan names for keywords matching plan type
              const planKeywords = {
                'Anual': ['anual', 'year', 'yearly', 'annually'],
                'Mensual': ['mensual', 'month', 'monthly']
              };
              
              const keywords = planKeywords[planType] || [];
              
              // Find selling plan that matches the plan type
              const matchingPlan = variant.selling_plan_allocations.find(allocation => {
                if (!allocation.selling_plan || !allocation.selling_plan.name) return false;
                const planName = allocation.selling_plan.name.toLowerCase();
                return keywords.some(keyword => planName.includes(keyword));
              });
              
              if (matchingPlan) {
                sellingPlanId = matchingPlan.selling_plan_id;
              } else {
                // Fallback to first available selling plan
                sellingPlanId = variant.selling_plan_allocations[0].selling_plan_id;
              }
            }
          }
        } catch (error) {
          console.warn('Could not fetch product data for selling plans:', error);
        }
      }

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      formData.append('properties[Plan]', planType);
      
      // Add selling plan ID if found (required for subscription products)
      if (sellingPlanId) {
        formData.append('selling_plan', sellingPlanId);
      }

      const addUrl = (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add';
      
      const response = await fetch(addUrl + '.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add to cart');
      }

      // Trigger cart update events
      document.dispatchEvent(new CustomEvent('theme:cart:change', {
        detail: { source: 'atra-membership' }
      }));
      document.dispatchEvent(new CustomEvent('cart:updated'));
      window.dispatchEvent(new CustomEvent('cartcount:update'));

      // Update cart if theme has cart update function
      if (window.theme && typeof window.theme.updateCart === 'function') {
        window.theme.updateCart();
      }

      // Trigger jQuery events if available
      if (window.jQuery) {
        jQuery(document).trigger('cart.requestComplete');
      }
    },

    trackEngagement() {
      const startTime = Date.now();

      window.addEventListener('beforeunload', () => {
        const timeSpent = Math.round((Date.now() - startTime) / 1000);
        this.trackEvent('time_on_page', { seconds: timeSpent });
      });

      document.querySelectorAll('.atra-faq__item').forEach(item => {
        item.addEventListener('toggle', () => {
          if (item.open) {
            const question = item.querySelector('.atra-faq__question').textContent;
            this.trackEvent('faq_opened', { question });
          }
        });
      });

      document.querySelectorAll('.atra-pricing__card').forEach(card => {
        card.addEventListener('mouseenter', () => {
          const planName = card.querySelector('.atra-pricing__plan-name').textContent;
          this.trackEvent('pricing_card_hover', { plan: planName });
        });
      });
    },

    trackEvent(eventName, data) {
      if (typeof gtag !== 'undefined') {
        gtag('event', eventName, data || {});
      }
      
      if (typeof fbq !== 'undefined') {
        fbq('trackCustom', eventName, data || {});
      }

      if (window.Shopify && Shopify.designMode) {
        console.log('ATRA Event:', eventName, data);
      }
    }
  };

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      AtraMembership.init();
    });
  } else {
    AtraMembership.init();
  }

  // Re-initialize when section is loaded in theme editor
  document.addEventListener('shopify:section:load', e => {
    if (e.target.querySelector('[data-section-type="atra-membership"]') || 
        e.detail && e.detail.sectionId && document.querySelector(`[data-section-id="${e.detail.sectionId}"]`)) {
      AtraMembership.init();
    }
  });
})();